import type { PlayerSnapshot, SocketMessage } from '@wyn/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { FIRST_STEPS, FIRST_STEPS_DONE_REWARD, firstStepRewardText } from '@wyn/game-core';
import { useEffect, useRef } from 'react';
import { Icon } from '../components/Icon.tsx';
import { UI } from '../icons/ui-icons.ts';
import { isMine } from '../lib/ids.ts';
import { addChatLine, deedsQuery, meQuery } from '../lib/queries.ts';
import { realtime, useFollow, useSocketStatus } from '../lib/realtime.ts';
import { rt } from '../lib/runtime.ts';
import { dismiss, toast, useToasts } from '../lib/toasts.ts';

/** Queries a Chronicle entry may have changed: coin, goods, offers, orders, who is about. */
const AFTER_EVENT = ['me', 'events', 'orders', 'offers', 'bank', 'town'];

/** Events told by a notice of their own, read off the snapshot, so a push doesn't tell them twice. */
const OWN_NOTICE = new Set(['deed', 'first-step', 'first-steps-done', 'notice-task', 'notice-board-done']);

/**
 * Keeps the socket open while a character is signed in, follows the realm and the player's town,
 * and turns each push into a cache update: a chat line is added, anything else is refetched.
 * When the socket comes or goes, everything is refetched once, which also switches polling off or on.
 */
export function RealtimeBridge() {
  const qc = useQueryClient();
  useRouterState({ select: (s) => s.location.pathname });
  const signedIn = rt().auth.isSignedIn();
  const me = useQuery({ ...meQuery(), enabled: signedIn });
  const p = me.data?.player ?? null;
  const status = useSocketStatus();

  useEffect(() => {
    if (!signedIn || !p) return;
    realtime().start();
    return () => realtime().stop();
  }, [signedIn, !!p]);

  const here = p && p.status.kind !== 'travelling' ? [`town:${p.town}`] : [];
  useFollow('shell', p ? ['realm', ...here] : []);

  useEffect(
    () =>
      realtime().onMessage((m: SocketMessage) => {
        switch (m.type) {
          case 'event':
            // Deeds, first steps and the notice board have their own notices (below), which also cover the player's own actions.
            if (!isMine(m.event.actionId) && !OWN_NOTICE.has(m.event.type)) toast(m.event.text);
            for (const key of AFTER_EVENT) void qc.invalidateQueries({ queryKey: [key] });
            break;
          case 'chat':
            addChatLine(qc, m.message);
            break;
          case 'book':
            void qc.invalidateQueries({ queryKey: ['book', m.itemKey, m.quality] });
            void qc.invalidateQueries({ queryKey: ['orders'] });
            break;
          case 'presence':
            void qc.invalidateQueries({ queryKey: ['town'] });
            break;
          case 'offers':
            void qc.invalidateQueries({ queryKey: ['offers'] });
            break;
          case 'following':
          case 'error':
            break;
        }
      }),
    [qc],
  );

  // Catch up on whatever was missed, and let every query re-read its polling interval.
  // A game or fight in progress keeps its own state: refetching it mid-play would reset the board.
  const was = useRef(status);
  useEffect(() => {
    const before = was.current;
    was.current = status;
    if (before === status || (before !== 'live' && status !== 'live')) return;
    void qc.invalidateQueries({ predicate: (q) => !['minigame-session', 'fight'].includes(String(q.queryKey[0])) });
  }, [status, qc]);

  // With no polling, bars would sit still: refetch when the soonest one ticks over.
  const skew = p ? p.serverTime - me.dataUpdatedAt : 0;
  const nextTick = p && status === 'live' ? Math.min(...Object.values(p.bars).map((b) => b.nextAt ?? Infinity)) : Infinity;
  useEffect(() => {
    if (!Number.isFinite(nextTick)) return;
    const id = setTimeout(() => void qc.invalidateQueries({ queryKey: ['me'] }), Math.max(1_000, nextTick - skew - Date.now() + 500));
    return () => clearTimeout(id);
  }, [nextTick, skew, qc]);

  useDeedNotices(p);
  useFirstStepNotices(p);
  useNoticeBoardNotices(p);

  return <Toasts />;
}

/**
 * A notice for each deed newly done, however the news came: an action's reply, a push, or a poll. Deeds
 * show up as new keys on the snapshot's record; their names come from the Deeds query, since hidden
 * deeds aren't in the bundle. The first snapshot after sign-in only sets the baseline.
 */
function useDeedNotices(p: PlayerSnapshot | null) {
  const qc = useQueryClient();
  const seen = useRef<{ id: string; keys: Set<string> } | null>(null);
  const deeds = p?.record.deeds;
  useEffect(() => {
    if (!p || !deeds) return;
    const was = seen.current;
    const keys = Object.keys(deeds);
    seen.current = { id: p.id, keys: new Set(keys) };
    const fresh = was && was.id === p.id ? keys.filter((k) => !was.keys.has(k)) : [];
    if (fresh.length === 0) return;
    void qc
      .fetchQuery({ ...deedsQuery(), staleTime: 0 })
      .then((d) => {
        for (const k of fresh) {
          const v = d.deeds.find((x) => x.key === k);
          toast(`A deed done: ${v?.name ?? 'a deed'}${v?.reward ? ` · ${v.reward}` : ''}`);
        }
      })
      .catch(() => toast(fresh.length === 1 ? 'A deed done.' : `${fresh.length} deeds done.`));
  }, [p?.id, deeds, qc]);
}

/**
 * A notice for each first step newly done, read off the snapshot as deeds are, and one more when the
 * chain is finished. A step's reward may be a good, so the pack is fetched again.
 */
function useFirstStepNotices(p: PlayerSnapshot | null) {
  const qc = useQueryClient();
  const seen = useRef<{ id: string; keys: Set<string> } | null>(null);
  const done = p?.firstSteps?.done;
  useEffect(() => {
    if (!p || !done) return;
    const was = seen.current;
    const keys = Object.keys(done);
    seen.current = { id: p.id, keys: new Set(keys) };
    const fresh = was && was.id === p.id ? FIRST_STEPS.filter((s) => keys.includes(s.key) && !was.keys.has(s.key)) : [];
    if (fresh.length === 0) return;
    for (const s of fresh) toast(`First steps: ${s.name} · ${firstStepRewardText(s.reward)}`);
    if (keys.length === FIRST_STEPS.length) toast(`You have found your feet in the realm · ${firstStepRewardText(FIRST_STEPS_DONE_REWARD)}`);
    void qc.invalidateQueries({ queryKey: ['me'] });
  }, [p?.id, done, qc]);
}

/**
 * A notice for each of the day's tasks newly done, read off the snapshot as deeds are, and one more when
 * all three are. A new day's board starts fresh without a notice.
 */
function useNoticeBoardNotices(p: PlayerSnapshot | null) {
  const seen = useRef<{ id: string; day: string; keys: Set<string>; done: boolean } | null>(null);
  const board = p?.board;
  useEffect(() => {
    if (!p || !board) return;
    const was = seen.current;
    const keys = board.tasks.filter((t) => t.done).map((t) => t.key);
    seen.current = { id: p.id, day: board.day, keys: new Set(keys), done: board.done };
    if (!was || was.id !== p.id || was.day !== board.day) return;
    for (const t of board.tasks) if (t.done && !was.keys.has(t.key)) toast(`Notice board: ${t.name} · +${board.reward} Renown`);
    if (board.done && !was.done) toast(`The day’s notices are all done · +${board.bonus} Renown`);
  }, [p?.id, board]);
}

function Toasts() {
  const list = useToasts();
  return (
    <section aria-label="Notices" aria-live="polite" className="pointer-events-none fixed right-4 bottom-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
      {list.map((t) => (
        <button
          key={t.id}
          type="button"
          data-testid="toast"
          onClick={() => dismiss(t.id)}
          className="pointer-events-auto border border-gilt/60 bg-vellum-raised px-3 py-2 text-left text-sm shadow-lg"
        >
          <Icon name={UI.notice} className="mr-2 text-gilt" />
          {t.text}
        </button>
      ))}
    </section>
  );
}

/** Footer note: whether pushes are arriving, or the page is polling. */
export function SocketBadge() {
  const status = useSocketStatus();
  return (
    <span data-testid="socket-status" title={status === 'live' ? 'Updates arrive as they happen.' : 'No live connection: checking every 30 seconds.'}>
      {status === 'live' ? (
        <span className="text-verdigris">● live</span>
      ) : status === 'connecting' ? (
        <span>◌ connecting…</span>
      ) : (
        <span>○ polling every 30 s</span>
      )}
    </span>
  );
}
